import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { setDirtyHandler, scopedKey } from "@/storage/session";
import { ALL_DATA_KEYS, readRawJSON, lastWrittenAt } from "@/storage/local";

// localStorage is the working store; Firestore is the copy that follows the
// user between devices. On sign-in we pull the cloud copy down, and every local
// write schedules a debounced push back up.
//
// Each blob lives in its own document (users/{uid}/data/{baseKey}) so a large
// routine cannot push the smaller blobs over Firestore's 1 MiB per-document
// limit. Values are stored as JSON strings because Firestore rejects nested
// arrays, which the routine and progress structures both contain.

const PUSH_DELAY_MS = 1500;
// Leaves headroom under Firestore's 1 MiB document cap for field overhead.
const MAX_VALUE_BYTES = 900 * 1024;

let activeUid = null;
let pendingKeys = new Set();
let pushTimer = null;
let onError = null;

function dataDoc(uid, baseKey) {
  return doc(db, "users", uid, "data", baseKey);
}

// Bloques que no caben en Firestore. Se recuerdan durante la sesión para que la
// bajada sepa que la nube tiene una copia vieja de ellos y no la imponga: es
// justo así como se perdió el progreso de una cuenta.
const tooBig = new Set();

async function pushKey(uid, baseKey) {
  const raw = localStorage.getItem(scopedKey(baseKey));
  const value = raw === null ? "" : raw;

  if (value.length > MAX_VALUE_BYTES) {
    tooBig.add(baseKey);
    onError?.(
      "Tus datos superan el límite de sincronización y este dispositivo no puede subirlos. " +
        "Las imágenes personalizadas de ejercicios son lo que más ocupa: bórralas para que vuelva a guardarse en la nube."
    );
    return;
  }

  tooBig.delete(baseKey);
  await setDoc(dataDoc(uid, baseKey), {
    value,
    updatedAt: Date.now(),
    // Cuándo se escribió en el dispositivo que la sube, no cuándo llegó. Es lo
    // que permite comparar dos copias sin fiarse del reloj del servidor.
    writtenAt: lastWrittenAt(baseKey),
  });
}

async function flush() {
  pushTimer = null;
  if (!activeUid || pendingKeys.size === 0) return;

  const keys = [...pendingKeys];
  pendingKeys = new Set();

  try {
    await Promise.all(keys.map((key) => pushKey(activeUid, key)));
  } catch (error) {
    // Put them back so the next write retries instead of losing the change.
    keys.forEach((key) => pendingKeys.add(key));
    console.error("[sync] no se pudieron guardar los cambios:", error);
    onError?.(
      error?.code === "permission-denied"
        ? "Sin permiso para guardar en la nube. Revisa las reglas de Firestore."
        : "No se han podido guardar los cambios en la nube. Se reintentará."
    );
  }
}

function schedulePush(baseKey) {
  if (!activeUid) return;
  pendingKeys.add(baseKey);
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(flush, PUSH_DELAY_MS);
}

// Antes de pisar un bloque local se guarda una copia aparte, que no se
// sincroniza ni se borra sola. Ocupa lo mismo que el dato y solo se conserva la
// última, pero es la diferencia entre un susto y una pérdida: sin ella, una
// bajada equivocada no deja nada que recuperar.
function keepRescueCopy(baseKey, previous) {
  if (previous === null) return;
  try {
    localStorage.setItem(scopedKey(`tryad_rescue_${baseKey}`), previous);
  } catch {
    // Si no hay espacio para la copia, se sigue: el dato de la nube es
    // igualmente el que el usuario espera ver.
  }
}

// Trae la copia de la nube a localStorage. Devuelve false cuando la cuenta aún
// no tiene datos en la nube, que es la señal para intentar migrar los locales.
//
// La regla es que **gana la escritura más reciente**, no la nube. Antes bajaba
// sin mirar, así que un dispositivo con trabajo sin subir —porque el bloque no
// cabía, porque falló el envío, porque se cerró la pestaña dentro de la ventana
// de espera— lo perdía entero en la siguiente recarga.
async function pullFromCloud(uid) {
  const snapshot = await getDocs(collection(db, "users", uid, "data"));
  if (snapshot.empty) return false;

  const outdated = [];

  snapshot.forEach((docSnap) => {
    const baseKey = docSnap.id;
    const { value, writtenAt } = docSnap.data();
    if (typeof value !== "string") return;

    const key = scopedKey(baseKey);
    const previous = localStorage.getItem(key);
    const localAt = lastWrittenAt(baseKey);
    const cloudAt = typeof writtenAt === "number" ? writtenAt : 0;

    // Nada local: no hay nada que perder.
    if (previous === null) {
      if (value !== "") localStorage.setItem(key, value);
      return;
    }

    // Idéntico: no se toca, y así la marca local se conserva.
    if (previous === value) return;

    // Este dispositivo tiene trabajo posterior al que subió el que escribió en
    // la nube, o de un bloque que se sabe que no cupo. Se queda lo local y se
    // reintenta subirlo.
    if ((localAt > 0 && localAt > cloudAt) || tooBig.has(baseKey)) {
      outdated.push(baseKey);
      return;
    }

    keepRescueCopy(baseKey, previous);
    if (value === "") localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  });

  // Se encolan al final para no reintentar dentro del propio recorrido.
  outdated.forEach((baseKey) => pendingKeys.add(baseKey));
  if (outdated.length > 0) {
    console.warn("[sync] la nube tenía una copia más antigua de:", outdated.join(", "));
  }

  return true;
}

// Looks for data written by the old localStorage-only auth, which scoped keys
// by email, and before that by nothing at all.
function findLegacyData(email) {
  const found = {};
  for (const baseKey of ALL_DATA_KEYS) {
    const candidates = email ? [`${baseKey}_${email}`, baseKey] : [baseKey];
    for (const key of candidates) {
      const value = localStorage.getItem(key);
      if (value !== null) {
        found[baseKey] = value;
        break;
      }
    }
  }
  return found;
}

async function migrateLegacyData(uid, email) {
  const legacy = findLegacyData(email);
  const keys = Object.keys(legacy);
  if (keys.length === 0) return false;

  keys.forEach((baseKey) => {
    localStorage.setItem(scopedKey(baseKey), legacy[baseKey]);
  });

  await Promise.all(keys.map((baseKey) => pushKey(uid, baseKey)));
  return true;
}

// Called once per sign-in, before the app reads any storage. Resolves to a
// short status the UI can report: what happened to the user's data.
export async function startSync(uid, email, { onSyncError } = {}) {
  activeUid = uid;
  onError = onSyncError || null;
  pendingKeys = new Set();
  tooBig.clear();

  let status = "empty";
  try {
    const hadCloudData = await pullFromCloud(uid);
    if (hadCloudData) {
      status = "restored";
    } else if (await migrateLegacyData(uid, email)) {
      status = "migrated";
    }
  } catch (error) {
    status = "offline";
    // A silent failure here looks identical to "new account with no data", and
    // the user would keep training against a local-only copy without knowing.
    console.error("[sync] no se pudo sincronizar con Firestore:", error);
    onError?.(
      error?.code === "permission-denied"
        ? "Sin permiso para acceder a tus datos en la nube. Revisa las reglas de Firestore."
        : "No se ha podido conectar con la nube. Tus datos se guardan solo en este dispositivo."
    );
  }

  setDirtyHandler(schedulePush);

  // Lo que la bajada dejó pendiente por ser más nuevo que la nube tiene que
  // subir ahora: si no, seguiría solo en este dispositivo y volvería a estar en
  // peligro en la siguiente recarga.
  if (pendingKeys.size > 0) {
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(flush, PUSH_DELAY_MS);
  }

  return status;
}

export function stopSync() {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = null;
  activeUid = null;
  pendingKeys = new Set();
  tooBig.clear();
  setDirtyHandler(null);
}

// Best-effort save when the tab is hidden or closed, so the last edit before
// leaving is not lost to the debounce window.
export function flushNow() {
  if (pushTimer) clearTimeout(pushTimer);
  return flush();
}

export async function hasCloudData(uid) {
  const snapshot = await getDoc(dataDoc(uid, ALL_DATA_KEYS[0]));
  return snapshot.exists();
}
